import { useCallback, useRef, useState } from 'react'
import {
  Text,
  Badge,
  Button,
  Collapse,
  GreyGrey60,
  GreyGrey70,
  IconChevronDown,
  IconChevronTop,
  IndigoIndigo60,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'

interface CustomerAliasesProps {
  aliases: Array<{ id: string; value: string }>
}

const THRESHOLD = 80

export const CustomerAliases = ({ aliases }: CustomerAliasesProps) => {
  const observerRef = useRef<ResizeObserver | null>(null)
  const [height, setHeight] = useState<number>(16)
  const [isExpanded, setIsExpanded] = useState(false)

  const measureRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    if (!node) {
      return
    }
    observerRef.current = new ResizeObserver(entries => {
      entries.forEach(entry => {
        setHeight(entry.contentRect.height)
      })
    })
    observerRef.current.observe(node)
  }, [])

  const handleToggle = () => setIsExpanded(!isExpanded)

  return (
    <>
      <Box mt="4px">
        <Text {...Primary13Regular} color={GreyGrey70}>
          Aliases
        </Text>
      </Box>

      {aliases?.length ? (
        <>
          <Box>
            <Collapse
              startingHeight={height > THRESHOLD ? THRESHOLD : height}
              in={isExpanded}
            >
              <Flex
                flexWrap="wrap"
                gap={1}
                justify="start"
                maxW="340px"
                overflow="hidden"
                ref={measureRef}
              >
                {aliases.map(({ value, id }) => (
                  <Badge
                    key={id}
                    size="sm"
                    sentiment="monospaced"
                    textTransform="none"
                  >
                    {value}
                  </Badge>
                ))}
              </Flex>
            </Collapse>
            {height > THRESHOLD && (
              <Button
                onClick={handleToggle}
                data-testid="customer-aliases.toggle"
                {...Primary13Regular}
                variant="ghost"
                style={{ padding: '0px', marginTop: '8px', height: 'auto' }}
                hover={{ boxShadow: 'none' }}
                trailingIcon={
                  isExpanded ? (
                    <IconChevronTop color={IndigoIndigo60} />
                  ) : (
                    <IconChevronDown color={IndigoIndigo60} />
                  )
                }
              >
                {isExpanded ? 'Hide additional aliases' : 'Show all aliases'}
              </Button>
            )}
          </Box>
        </>
      ) : (
        <Box mt="4px">
          <Text {...Primary13Regular} color={GreyGrey60}>
            Not set
          </Text>
        </Box>
      )}
    </>
  )
}
